'use client'

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import Icon from '../../atoms/Icon/Icon.component'
import Button from '../../atoms/Button/Button.component'
import { SectionThemeContext } from '../../../contexts/SectionTheme.context'
import * as S from './Stepper.styles'
import { Paragraph } from '../../atoms/Typography/Typography.component'

type AnimationPhase = 'idle' | 'exiting' | 'entering'

type StepperContextType = {
  value: number
  totalSteps: number
  onValueChange: (step: number) => void
  animated: boolean
  animationPhase: AnimationPhase
  displayedStep: number
  onPhaseComplete: () => void
  stepTitles: string[]
}

const StepperContext = createContext<StepperContextType | null>(null)

function useStepperContext() {
  const context = useContext(StepperContext)
  if (!context) {
    throw new Error('Stepper compound components must be used within a <Stepper> parent.')
  }
  return context
}

// --- Stepper (root) ---
type StepperProps = {
  value: number
  onValueChange: (step: number) => void
  animated?: boolean
  children: React.ReactNode
}

function Stepper({ value, onValueChange, animated = false, children }: StepperProps) {
  const { selectedColorScheme } = useContext(SectionThemeContext)
  const [animationPhase, setAnimationPhase] = useState<AnimationPhase>('idle')
  const [displayedStep, setDisplayedStep] = useState(value)
  const prevValueRef = useRef(value)

  useEffect(() => {
    if (prevValueRef.current === value) return
    prevValueRef.current = value

    if (!animated) {
      setDisplayedStep(value)
      return
    }

    setAnimationPhase('exiting')
  }, [value, animated])

  const onPhaseComplete = useCallback(() => {
    setAnimationPhase((current) => {
      if (current === 'exiting') {
        setDisplayedStep(value)
        return 'entering'
      }
      return 'idle'
    })
  }, [value])

  const { totalSteps, stepTitles } = useMemo(() => {
    let count = 0
    const titles: string[] = []
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child) && child.type === StepperContent) {
        count++
      }
      if (!React.isValidElement<{ children?: React.ReactNode }>(child) || child.type !== StepperList) return

      React.Children.forEach(child.props.children, (stepChild) => {
        if (!React.isValidElement<{ children?: React.ReactNode }>(stepChild) || stepChild.type !== StepperStep) return

        const text = typeof stepChild.props.children === 'string'
          ? stepChild.props.children
          : ''
        titles.push(text)
      })
    })
    return { totalSteps: count, stepTitles: titles }
  }, [children])

  const [announcement, setAnnouncement] = useState('')

  useEffect(() => {
    const title = stepTitles[value] || ''
    setAnnouncement(`${value + 1} / ${totalSteps}: ${title}`)
  }, [value, totalSteps, stepTitles])

  const contextValue = useMemo(
    () => ({ value, totalSteps, onValueChange, animated, animationPhase, displayedStep, onPhaseComplete, stepTitles }),
    [value, totalSteps, onValueChange, animated, animationPhase, displayedStep, onPhaseComplete, stepTitles]
  )

  return (
    <StepperContext.Provider value={contextValue}>
      <S.StepperWrapper colorScheme={selectedColorScheme}>
        <S.VisuallyHidden aria-live="polite" role="status">
          {announcement}
        </S.VisuallyHidden>
        {children}
      </S.StepperWrapper>
    </StepperContext.Provider>
  )
}

// --- StepperList ---
type StepperListProps = {
  'aria-label'?: string
  children: React.ReactNode
}

function StepperList({ 'aria-label': ariaLabel, children }: StepperListProps) {
  return (
    <S.StepperListWrapper role="list" aria-label={ariaLabel}>
      {children}
    </S.StepperListWrapper>
  )
}

// --- StepperStep ---
type StepperStepProps = {
  value: number
  children: React.ReactNode
}

function StepperStep({ value, children }: StepperStepProps) {
  const { value: currentStep } = useStepperContext()
  const { selectedColorScheme } = useContext(SectionThemeContext)

  const status = value < currentStep ? 'completed' : value === currentStep ? 'active' : 'pending'

  return (
    <S.StepperStepWrapper
      $status={status}
      colorScheme={selectedColorScheme}
      aria-current={status === 'active' ? 'step' : undefined}
    >
      <S.StepperProgressBar $status={status} colorScheme={selectedColorScheme} />
      <S.StepperStepLabel $status={status} colorScheme={selectedColorScheme}>
        {status === 'completed' &&
          <S.StepperIcon colorScheme={selectedColorScheme}>
            <Icon name="check" size="small" aria-hidden />
          </S.StepperIcon>
        }
        <Paragraph typo="paragraphLG">{children}</Paragraph>
      </S.StepperStepLabel>
    </S.StepperStepWrapper>
  )
}

// --- StepperContent ---
type StepperContentProps = {
  value: number
  children: React.ReactNode
}

function StepperContent({ value, children }: StepperContentProps) {
  const { value: currentStep, animated, animationPhase, displayedStep, onPhaseComplete } = useStepperContext()

  if (!animated) {
    if (value !== currentStep) return null
    return <S.StepperContentWrapper>{children}</S.StepperContentWrapper>
  }

  if (value !== displayedStep) return null

  const phase = animationPhase === 'idle' ? undefined : animationPhase

  return (
    <S.StepperContentWrapper $animationPhase={phase} onAnimationEnd={onPhaseComplete}>
      {children}
    </S.StepperContentWrapper>
  )
}

// --- StepperControls ---
type StepperControlsProps = {
  prevLabel?: string
  nextLabel?: string
  submitLabel?: string
  isSubmitting?: boolean
  onPrev?: () => void
  onNext?: () => void
}

function StepperControls({ prevLabel = 'Indietro', nextLabel = 'Prosegui', submitLabel, isSubmitting, onPrev, onNext }: StepperControlsProps) {
  const { value: currentStep, totalSteps, onValueChange } = useStepperContext()
  const { selectedColorScheme } = useContext(SectionThemeContext)

  const isFirst = currentStep === 0
  const isLast = currentStep === totalSteps - 1

  const handlePrev = () => {
    if (onPrev) {
      onPrev()
    } else if (!isFirst) {
      onValueChange(currentStep - 1)
    }
  }

  const handleNext = () => {
    if (onNext) {
      onNext()
    } else if (!isLast) {
      onValueChange(currentStep + 1)
    }
  }

  return (
    <S.StepperControlsWrapper>
      <Button
        disabled={isFirst}
        label={prevLabel}
        variant="primary"
        iconStart="arrow-left"
        handleClick={handlePrev}
        inheritedColorScheme={selectedColorScheme}
      />
      
      {isLast && submitLabel ? (
        <Button
          key="submit"
          type="submit"
          label={submitLabel}
          variant="primary"
          iconEnd="arrow-right"
          inheritedColorScheme={selectedColorScheme}
          disabled={isSubmitting}
        />
      ) : (
        <Button
          key="next"
          type="button"
          label={nextLabel}
          variant="primary"
          iconEnd="arrow-right"
          handleClick={handleNext}
          inheritedColorScheme={selectedColorScheme}
        />
      )}
    </S.StepperControlsWrapper>
  )
}

export { Stepper, StepperList, StepperStep, StepperContent, StepperControls }
export default Stepper
